/* sk-datalayer.js: the store's dataLayer events for GTM.
 *
 * Source: "[INT] SK Tracking Document - created Sep 2026 - [Shop] Implementation Document"
 * (the 30 Sep revision, with a Dev Requirements snippet per event). Loaded by
 * snippets/sk-datalayer.liquid only when the sk_gtm_id theme setting is set. That snippet has
 * already set window.skDL (page context, product, cart).
 *
 * Conventions follow the corporate site, which shares the container
 * (apps/corporate-astro/src/lib/analytics in the stone-kite repo):
 *
 * IDENTITY. logged_in_status and user_id come from the account app, never from Shopify's
 * customer login or the sk_account cookie: GET /api/account/analytics-id on
 * account.stonekite.com, once per page, only with Cookiebot statistics consent, at most 1.5
 * seconds (docs/analytics/corporate-user-id.md in happycog/stonekite-app). Signed out, and every
 * failure, is { logged_in_status: 'not-logged-in', user_id: undefined }. Consent given later on
 * the page fetches it then; a refusal puts every later push back to signed out.
 *
 * ORDER. initialize_dataLayer and page_view go first, both with the page facts, once the
 * identity is known. Everything else starts after them.
 *
 * VALUES. A parameter that doesn't apply is undefined, as the document asks ("undefined/omit").
 * The key stays on the push with an undefined value rather than being left out: GTM merges
 * pushes into one data model, and a missing key would keep the previous push's value. Inside
 * `ecommerce`, a field that doesn't apply is left out, and `ecommerce` is cleared before every
 * ecommerce push, the way the document and Google's GA4 guide show.
 *
 * ITEMS. The document's item fields have no Shopify field of their own, so itemFields() derives
 * them from each product's tags, type, title and handle. It is the one place that knows the
 * store's catalogue conventions (SERIES, CATEGORIES below); cards, the product page and cart
 * lines all go through it.
 *
 * NOT HERE, because a theme can't see Shopify's checkout: purchase, add_shipping_info and
 * add_payment_info. begin_checkout fires on the click that leaves the store for checkout, which
 * is how the document now defines it.
 *
 * CART EVENTS come from the cart itself, not from clicks. Every add, change and remove request
 * (the theme's and PreProduct's) is watched by the snippet; after each one this compares the
 * cart with the last copy it saw. A line that went up is add_to_cart, a line that went down is
 * remove_from_cart. That catches only adds that succeeded, and gets the quantity right: Shopify's
 * add response reports the line's new total, not how many were just added.
 */
(function () {
  var DL = window.skDL;
  if (!DL) return;
  window.dataLayer = window.dataLayer || [];

  var DOMAIN_TYPE = 'shopify';
  var ANALYTICS_ID_URL = 'https://account.stonekite.com/api/account/analytics-id';
  var SIGNED_OUT = { logged_in_status: 'not-logged-in', user_id: undefined };

  /* The store's catalogue conventions, for itemFields(). A new series needs its tag here. */
  var SERIES = { onezeroone: 'one zero one' }; // product tag -> item_brand
  var CATEGORIES = { 'comic book': 'comic', 'art print': 'art print' }; // product type -> item_category

  var nativeFetch = DL.nativeFetch || window.fetch;
  var currency = DL.currency || (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || undefined;

  /* ---------------------------------------------------------------------------------------
   * Identity
   * ------------------------------------------------------------------------------------- */
  var identity = SIGNED_OUT;

  function parseIdentity(body) {
    if (
      body &&
      body.logged_in_status === 'logged-in' &&
      typeof body.user_id === 'string' &&
      body.user_id !== '' &&
      body.user_id !== 'null'
    ) {
      return { logged_in_status: 'logged-in', user_id: body.user_id };
    }
    return SIGNED_OUT;
  }

  /* One call, never retried, never longer than 1.5 seconds. credentials: 'include' or the
   * browser sends no cookies; no request headers, so it stays a simple CORS request (the
   * endpoint doesn't answer a preflight). */
  function readIdentity() {
    return new Promise(function (resolve) {
      var done = false;
      var controller = window.AbortController ? new AbortController() : null;
      var timer = setTimeout(function () {
        if (controller) controller.abort();
        finish(SIGNED_OUT);
      }, 1500);
      function finish(id) {
        if (done) return;
        done = true;
        clearTimeout(timer);
        resolve(id);
      }
      try {
        var opts = { credentials: 'include', cache: 'no-store' };
        if (controller) opts.signal = controller.signal;
        nativeFetch(ANALYTICS_ID_URL, opts)
          .then(function (r) {
            return r.ok ? r.json().then(parseIdentity) : SIGNED_OUT;
          })
          .then(finish, function () {
            finish(SIGNED_OUT);
          });
      } catch (e) {
        finish(SIGNED_OUT);
      }
    });
  }

  function hasStatisticsConsent() {
    var c = window.Cookiebot;
    return !!(c && c.consent && c.consent.statistics === true);
  }

  // Once per page load, whichever of the load and a later consent gets there.
  var identified = null;
  function identify() {
    if (!identified) {
      identified = readIdentity().then(function (id) {
        identity = id;
      });
    }
    return identified;
  }
  // CookiebotOnAccept also fires on a returning visitor's load, which the check below may
  // already have handled.
  window.addEventListener('CookiebotOnAccept', function () {
    if (hasStatisticsConsent()) identify();
    else identity = SIGNED_OUT;
  });
  window.addEventListener('CookiebotOnDecline', function () {
    identity = SIGNED_OUT;
  });

  /* ---------------------------------------------------------------------------------------
   * Pushing
   * ------------------------------------------------------------------------------------- */
  function blank(v) {
    return v === undefined || v === null || v === '' || (typeof v === 'number' && isNaN(v));
  }

  function clean(obj) {
    var out = {};
    for (var k in obj) {
      if (!Object.prototype.hasOwnProperty.call(obj, k) || blank(obj[k])) continue;
      out[k] = Array.isArray(obj[k]) ? obj[k].map(clean) : obj[k];
    }
    return out;
  }

  function push(event, params, ecommerce) {
    var o = { event: event };
    for (var k in params) {
      if (Object.prototype.hasOwnProperty.call(params, k)) o[k] = blank(params[k]) ? undefined : params[k];
    }
    o.logged_in_status = identity.logged_in_status;
    o.user_id = identity.user_id;
    o.domain_type = DOMAIN_TYPE;
    if (ecommerce) {
      window.dataLayer.push({ ecommerce: null });
      o.ecommerce = clean(ecommerce);
    }
    window.dataLayer.push(o);
  }

  function money(cents) {
    return cents == null ? null : Math.round(Number(cents)) / 100;
  }

  function round(n) {
    return Math.round(n * 100) / 100;
  }

  function nz(v) {
    return blank(v) ? null : v;
  }

  /* Visible text only. innerText skips display:none, so a tooltip that is hidden until hover
   * doesn't end up as the click text; visually-hidden labels still count. */
  function textOf(el) {
    var t = el && (el.innerText || '').replace(/\s+/g, ' ').trim();
    return t || null;
  }

  /* A link's text as the corporate site reads it (textContent). */
  function linkText(a) {
    var t = (a.textContent || '').replace(/\s+/g, ' ').trim();
    return t || null;
  }

  /* What a visitor clicked: its text, else its aria-label, else an image's alt inside it. */
  function triggerText(el) {
    if (!el) return null;
    var img = el.querySelector && el.querySelector('img[alt]');
    return (
      textOf(el) ||
      (el.getAttribute('aria-label') || '').trim() ||
      (img && (img.getAttribute('alt') || '').trim()) ||
      null
    );
  }

  /* A hostname as the document writes click_domain: no www. */
  function bareHost(host) {
    return (host || '').replace(/^www\./i, '') || null;
  }

  /* List ids in the document's style: 'item_detail_related_products'. */
  function slug(s) {
    return s
      ? String(s)
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '_')
          .replace(/^_|_$/g, '')
      : null;
  }

  function sum(items) {
    var v = 0;
    for (var i = 0; i < items.length; i++) v += (items[i].price || 0) * (items[i].quantity || 1);
    return round(v);
  }

  function tagsOf(v) {
    if (Array.isArray(v)) return v;
    return v ? String(v).split(',') : [];
  }

  /* ---------------------------------------------------------------------------------------
   * Item fields
   * ------------------------------------------------------------------------------------- */

  /* The document's item_brand, item_category to item_category4 and item_variant, from what
   * Shopify has: tags, product type, title and handle. Every product today has one variant
   * ("Default Title"), so the variant name comes from the title:
   *   "ONE ZERO ONE #1 - Variant - Tula Lotay" (handle ...-variant-2) -> "tula lotay variant 2"
   *   "ONE ZERO ONE #1 - Main - Björn Barends"                    -> "björn barends main cover"
   *   "ONE ZERO ONE Exclusive Launch Art Print (18x24 in)"         -> "18x24 in"
   * A real variant title wins over all of that. */
  function itemFields(p) {
    var tags = tagsOf(p.tags);
    var title = p.title || '';
    var type = (p.type || '').toLowerCase();
    var i, m;

    var brand = null;
    for (i = 0; i < tags.length; i++) if (SERIES[tags[i]]) brand = SERIES[tags[i]];

    var isBundle = type === 'bundle' || tags.indexOf('bundle') !== -1;
    var comicTags = tags.indexOf('cover-main') !== -1 || tags.indexOf('cover-variant') !== -1;
    var category = CATEGORIES[type] || (isBundle && comicTags ? 'comic' : type || null);

    var issue = null;
    for (i = 0; i < tags.length; i++) {
      m = tags[i].match(/^sk-issue-(\d+)$/);
      if (m) issue = 'issue ' + m[1];
    }
    if (!issue && (m = title.match(/issues?\s*(\d+)\s*-\s*(\d+)/i))) issue = 'issues ' + m[1] + '-' + m[2];
    if (!issue && (m = title.match(/#\s*(\d+)/))) issue = 'issue ' + m[1];

    var variant = p.variantTitle && p.variantTitle !== 'Default Title' ? p.variantTitle : null;
    if (!variant && (m = title.match(/-\s*variant\s*-\s*(.+)$/i))) {
      var n = (p.handle || '').match(/variant-(\d+)/);
      variant = m[1] + ' variant' + (n ? ' ' + n[1] : '');
    }
    if (!variant && (m = title.match(/-\s*main\s*-\s*(.+)$/i))) variant = m[1] + ' main cover';
    if (!variant && (m = title.match(/\(([^)]+)\)\s*$/))) variant = m[1];

    return {
      item_brand: brand,
      item_category: category,
      item_category2: issue,
      item_category3: isBundle ? 'bundle' : 'single product',
      item_category4: p.status || null,
      item_variant: variant ? variant.toLowerCase() : null,
    };
  }

  /* One item, in the document's key order. */
  function makeItem(base, fields, list) {
    return {
      item_id: base.id,
      item_name: base.name,
      discount: base.discount,
      index: list && list.index,
      item_brand: fields.item_brand,
      item_category: fields.item_category,
      item_category2: fields.item_category2,
      item_category3: fields.item_category3,
      item_category4: fields.item_category4,
      item_list_id: list && list.id,
      item_list_name: list && list.name,
      item_variant: fields.item_variant,
      price: base.price,
      quantity: base.quantity,
    };
  }

  /* ---------------------------------------------------------------------------------------
   * What each product is, for cart lines: /cart.js carries no tags or compare-at price. Filled
   * from the page (the product, the cart, the cards), then from /products/{handle}.js for any
   * product the page doesn't show, cached for the tab's session.
   * ------------------------------------------------------------------------------------- */
  var INFO_KEY = 'sk-dl-products';
  var info = {};
  try {
    info = JSON.parse(sessionStorage.getItem(INFO_KEY) || '{}') || {};
  } catch (e) {}
  function saveInfo() {
    try {
      sessionStorage.setItem(INFO_KEY, JSON.stringify(info));
    } catch (e) {}
  }
  function learnProduct(p) {
    var compare = {};
    for (var i = 0; i < (p.variants || []).length; i++) compare[p.variants[i].id] = p.variants[i].compare_at_price || null;
    info[p.id] = {
      tags: tagsOf(p.tags),
      preorder: !!(p.preorder || p.requires_selling_plan || (p.selling_plan_groups && p.selling_plan_groups.length)),
      compare: compare,
      complete: true,
    };
  }
  if (DL.product) learnProduct(DL.product);
  (function () {
    var lines = (DL.cart && DL.cart.items) || [];
    for (var i = 0; i < lines.length; i++) {
      var l = lines[i];
      var known = info[l.product_id] || { compare: {} };
      known.tags = tagsOf(l.tags);
      known.preorder = !!l.preorder;
      known.compare = known.compare || {};
      known.compare[l.variant_id] = l.compare_at_price || null;
      info[l.product_id] = known;
    }
  })();
  saveInfo();

  function ensureInfo(lines) {
    var wanted = [];
    for (var i = 0; i < lines.length; i++) {
      var l = lines[i];
      var known = info[l.product_id];
      if (!known || !known.compare || !(l.variant_id in known.compare)) wanted.push(l);
    }
    return Promise.all(
      wanted.map(function (l) {
        if (!l.handle) return null;
        return nativeFetch('/products/' + encodeURIComponent(l.handle) + '.js', { credentials: 'same-origin' })
          .then(function (r) {
            return r.ok ? r.json() : null;
          })
          .then(function (p) {
            if (p) learnProduct(p);
          })
          .catch(function () {});
      }),
    ).then(saveInfo);
  }

  /* ---------------------------------------------------------------------------------------
   * Items from each source
   * ------------------------------------------------------------------------------------- */
  var LISTS_KEY = 'sk-dl-lists';
  function readLists() {
    try {
      return JSON.parse(sessionStorage.getItem(LISTS_KEY) || '{}') || {};
    } catch (e) {
      return {};
    }
  }
  function rememberList(productId, list) {
    if (!productId) return;
    try {
      var all = readLists();
      all[productId] = list;
      sessionStorage.setItem(LISTS_KEY, JSON.stringify(all));
    } catch (e) {}
  }

  /* A card stamped by snippets/sk-dl-item.liquid. */
  function itemFromCard(el, list) {
    var price = parseFloat(el.getAttribute('data-sk-item-price'));
    var compare = parseFloat(el.getAttribute('data-sk-item-compare'));
    var fields = itemFields({
      title: el.getAttribute('data-sk-item-name'),
      handle: el.getAttribute('data-sk-item-handle'),
      type: el.getAttribute('data-sk-item-type'),
      tags: el.getAttribute('data-sk-item-tags'),
      variantTitle: el.getAttribute('data-sk-item-variant'),
      status: el.getAttribute('data-sk-item-status'),
    });
    return makeItem(
      {
        id: nz(el.getAttribute('data-sk-item-id')),
        name: nz(el.getAttribute('data-sk-item-name')),
        discount: !isNaN(compare) && !isNaN(price) && compare > price ? round(compare - price) : null,
        price: isNaN(price) ? null : price,
        quantity: 1,
      },
      fields,
      list,
    );
  }

  /* A cart line, from /cart.js or the Liquid cart, and how many of it this event is about.
   * discount is per unit: below the compare-at price, plus any discount code on the line. */
  function itemFromLine(line, quantity) {
    var known = info[line.product_id] || {};
    var list = readLists()[line.product_id] || null;
    var compare = known.compare ? known.compare[line.variant_id] : null;
    var off = 0;
    if (compare && line.original_price != null && compare > line.original_price) off += compare - line.original_price;
    if (line.original_price != null && line.final_price != null && line.original_price > line.final_price) {
      off += line.original_price - line.final_price;
    }
    var fields = itemFields({
      title: line.product_title,
      handle: line.handle,
      type: line.product_type,
      tags: known.tags,
      variantTitle: line.variant_title,
      status: line.selling_plan_allocation || known.preorder ? 'pre-order' : 'buy-now',
    });
    return makeItem(
      {
        id: String(line.sku || line.variant_id),
        name: nz(line.product_title),
        discount: off > 0 ? money(off) : null,
        price: money(line.final_price),
        quantity: quantity == null ? line.quantity : quantity,
      },
      fields,
      list,
    );
  }

  /* The order-level coupon: every discount title on the cart, cart-level and line-level. */
  function cartCoupon(cart) {
    var titles = [];
    function add(t) {
      if (t && titles.indexOf(t) === -1) titles.push(t);
    }
    var apps = (cart && cart.cart_level_discount_applications) || [];
    for (var i = 0; i < apps.length; i++) add(apps[i].title);
    var items = (cart && cart.items) || [];
    for (var j = 0; j < items.length; j++) {
      for (var k = 0; k < (items[j].discounts || []).length; k++) add(items[j].discounts[k].title);
    }
    return titles.length ? titles.join(',') : null;
  }

  function linesOf(cart) {
    var out = [];
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) out.push(itemFromLine(items[i]));
    return out;
  }

  function selectedVariant(p) {
    var id = new URLSearchParams(location.search).get('variant');
    if (!id) {
      var input = document.querySelector('form[action*="/cart/add"] [name="id"]');
      if (input && input.value) id = input.value;
    }
    id = id || p.selected;
    for (var i = 0; i < p.variants.length; i++) if (String(p.variants[i].id) === String(id)) return p.variants[i];
    return p.variants[0] || null;
  }

  function listOf(el) {
    var name = el.getAttribute('data-sk-item-list') || null;
    return { id: el.getAttribute('data-sk-item-list-id') || slug(name), name: name };
  }

  function isExternal(a) {
    if (a.protocol === 'mailto:') return true;
    if (a.protocol !== 'http:' && a.protocol !== 'https:') return false;
    var host = (a.hostname || '').toLowerCase();
    if (!host || host === location.hostname.toLowerCase()) return false;
    return host !== 'stonekite.com' && !/\.stonekite\.com$/.test(host);
  }

  /* How far down the page the bottom of the viewport has reached, 0 to 100. Within 2px of the
   * bottom counts as the bottom. */
  function scrollPercent(scrollY, viewport, height) {
    if (height <= viewport) return 100;
    var seen = scrollY + viewport;
    if (seen >= height - 2) return 100;
    return Math.max(0, Math.min(100, (seen / height) * 100));
  }

  /* ---------------------------------------------------------------------------------------
   * The page
   * ------------------------------------------------------------------------------------- */
  function run() {
    var pageFacts = {
      page_url: location.host + location.pathname + location.search,
      page_title: document.title,
      query_parameter: location.search && location.search !== '?' ? location.search : undefined,
      page_type: DL.page_type,
      publish_date: DL.publish_date,
    };
    push('initialize_dataLayer', pageFacts);
    push('page_view', pageFacts);

    /* view_item: the product page, with the variant the shopper has selected. index and the
     * list only when the shopper arrived from a list. */
    if (DL.product) {
      var p = DL.product;
      var v = selectedVariant(p);
      if (v) {
        var status = !v.available ? 'out-of-stock' : v.requires_selling_plan || p.preorder ? 'pre-order' : 'buy-now';
        var item = makeItem(
          {
            id: String(v.sku || v.id),
            name: nz(p.title),
            discount: v.compare_at_price && v.compare_at_price > v.price ? money(v.compare_at_price - v.price) : null,
            price: money(v.price),
            quantity: 1,
          },
          itemFields({ title: p.title, handle: p.handle, type: p.type, tags: p.tags, variantTitle: v.title, status: status }),
          readLists()[p.id] || null,
        );
        push('view_item', {}, { currency: currency, value: item.price, items: [item] });
      }
    }

    /* view_item_list: each product list on the page, once: homepage rows, collection grids
     * (including /pages/comics), search results, the product page's variant covers, and "You
     * may also like", which Shopify renders after load. A list that isn't displayed is skipped:
     * the product page renders its variant list twice (one copy per layout) and shows one. Lists
     * marked data-sk-item-select-only (the homepage hero, the bundle offer) are a single
     * featured product, so they count for select_item only. An empty list still fires. */
    function viewLists(root) {
      var found = [];
      if (root.matches && root.matches('[data-sk-item-list]')) found.push(root);
      var inner = root.querySelectorAll ? root.querySelectorAll('[data-sk-item-list]') : [];
      for (var f = 0; f < inner.length; f++) found.push(inner[f]);
      for (var li = 0; li < found.length; li++) {
        var el = found[li];
        if (el.hasAttribute('data-sk-item-select-only') || el.hasAttribute('data-sk-list-viewed')) continue;
        if (!el.getClientRects().length) continue;
        el.setAttribute('data-sk-list-viewed', '');
        var l = listOf(el);
        var cards = el.querySelectorAll('[data-sk-item]');
        var items = [];
        for (var ci = 0; ci < cards.length; ci++) {
          items.push(itemFromCard(cards[ci], { id: l.id, name: l.name, index: ci + 1 }));
        }
        push('view_item_list', {}, { currency: currency, item_list_id: l.id, item_list_name: l.name, items: items });
      }
    }
    viewLists(document);

    /* view_cart: the cart page. Tax and shipping aren't known until checkout, so they're left
     * out, as the document allows. */
    var cart = DL.cart || null;
    if (DL.page_type === 'cart' && cart) {
      push(
        'view_cart',
        {},
        {
          currency: cart.currency || currency,
          value: money(cart.total_price),
          coupon: cartCoupon(cart),
          items: linesOf(cart),
        },
      );
    }

    /* add_to_cart / remove_from_cart, from the cart's own changes (see the header). */
    function diff(before, after) {
      var was = {};
      var i;
      for (i = 0; i < ((before && before.items) || []).length; i++) was[before.items[i].key] = before.items[i];
      var up = [];
      var down = [];
      var seen = {};
      for (i = 0; i < ((after && after.items) || []).length; i++) {
        var line = after.items[i];
        var prev = was[line.key];
        seen[line.key] = true;
        var delta = line.quantity - (prev ? prev.quantity : 0);
        if (delta > 0) up.push({ line: line, qty: delta });
        if (delta < 0) down.push({ line: line, qty: -delta });
      }
      for (var key in was) {
        if (Object.prototype.hasOwnProperty.call(was, key) && !seen[key]) down.push({ line: was[key], qty: was[key].quantity });
      }
      if (!up.length && !down.length) return Promise.resolve();
      var all = up.concat(down).map(function (c) {
        return c.line;
      });
      var cur = (after && after.currency) || currency;
      return ensureInfo(all).then(function () {
        var added = up.map(function (c) {
          return itemFromLine(c.line, c.qty);
        });
        var removed = down.map(function (c) {
          return itemFromLine(c.line, c.qty);
        });
        if (added.length) push('add_to_cart', {}, { currency: cur, value: sum(added), items: added });
        if (removed.length) push('remove_from_cart', {}, { currency: cur, value: sum(removed), items: removed });
      });
    }

    /* One cart comparison at a time, in the order the requests finished. */
    var chain = Promise.resolve();
    function onCart(entry) {
      chain = chain
        .then(function () {
          var data = entry.data;
          if (entry.type !== 'add' && data && data.items && data.token) return data;
          return nativeFetch('/cart.js', { credentials: 'same-origin' }).then(function (r) {
            return r.json();
          });
        })
        .then(function (next) {
          if (!next || !next.items) return;
          var before = cart;
          cart = next;
          return diff(before, next);
        })
        .catch(function () {});
    }
    var queued = window.skDLCartQueue || [];
    window.skDLOnCart = onCart;
    for (var qi = 0; qi < queued.length; qi++) onCart(queued[qi]);
    queued.length = 0;

    /* Clicks */
    var lastClick = { el: null, at: 0 };

    document.addEventListener(
      'click',
      function (e) {
        var t = e.target;
        if (!t || !t.closest) return;

        lastClick = { el: t.closest('a, button, [role="button"]'), at: Date.now() };

        var a = t.closest('a[href]');

        /* click_to_login: the header's account link while logged out, marked
         * data-click-to-login. Never also an external_link_click. */
        if (a && a.hasAttribute('data-click-to-login')) {
          push('click_to_login', { click_text: linkText(a) });
        } else if (a && isExternal(a)) {
          var text = linkText(a);
          push('external_link_click', {
            click_text: text,
            click_id: text ? undefined : a.id || (a.getAttribute('aria-label') || '').trim(),
            click_url: a.href,
            click_domain: a.protocol === 'mailto:' ? 'mailto' : bareHost(a.hostname),
          });
        }

        /* social_share: the product page's share control. Copying the link is "share-link".
         * Where the browser has a native share sheet, the platform the shopper picks isn't
         * reported back, so that is "native-share". */
        var share = t.closest('share-button');
        if (share) {
          var button = t.closest('button');
          if (t.closest('.share-button__copy')) {
            push('social_share', { click_url: 'copy-share-link', click_domain: 'share-link' });
          } else if (button && button.parentElement === share) {
            var input = share.querySelector('input');
            push('social_share', { click_url: (input && input.value) || location.href, click_domain: 'native-share' });
          }
        }

        /* featured_cta_click: the homepage hero's CTAs, title link and cover art, marked
         * data-sk-cta. The art link has no text, so click_text falls back to the image's alt. */
        var cta = t.closest('[data-sk-cta]');
        if (cta) {
          push('featured_cta_click', {
            click_text: triggerText(cta),
            click_url: cta.href,
            element_name: cta.getAttribute('data-sk-cta'),
          });
        }

        /* banner_click: a link or button on a banner marked data-sk-banner (the homepage
         * announcement). The dismiss button has no URL. */
        var bannerControl = t.closest('a[href], button');
        var banner = bannerControl && bannerControl.closest('[data-sk-banner]');
        if (banner) {
          push('banner_click', {
            click_text: triggerText(bannerControl),
            click_url: bannerControl.href,
            element_name: banner.getAttribute('data-sk-banner'),
          });
        }

        /* select_item: a link to a product page inside a product list (a list can be one
         * featured product, like the homepage hero). */
        var card = a && /\/products\//.test(a.pathname) && a.closest('[data-sk-item]');
        var listEl = card && card.closest('[data-sk-item-list]');
        if (card && listEl) {
          var index = Array.prototype.indexOf.call(listEl.querySelectorAll('[data-sk-item]'), card) + 1;
          var lst = listOf(listEl);
          var picked = itemFromCard(card, { id: lst.id, name: lst.name, index: index });
          rememberList(card.getAttribute('data-sk-item-product'), { id: lst.id, name: lst.name, index: index });
          push('select_item', {}, { currency: currency, item_list_id: lst.id, item_list_name: lst.name, items: [picked] });
        }

        /* begin_checkout: the click that leaves the cart for Shopify's checkout, whose first
         * step is contact. */
        if (t.closest('[name="checkout"], a[href*="/checkout"]') && cart) {
          push(
            'begin_checkout',
            { checkout_step_number: '1', checkout_step_name: 'contact' },
            {
              currency: cart.currency || currency,
              value: money(cart.total_price),
              coupon: cartCoupon(cart),
              items: linesOf(cart),
            },
          );
        }
      },
      true,
    );

    /* banner_visible: once per banner per page, when half of it is on screen. A dismissed
     * banner is display:none and never counts. */
    var banners = document.querySelectorAll('[data-sk-banner]');
    if (banners.length && 'IntersectionObserver' in window) {
      var io = new IntersectionObserver(
        function (entries) {
          for (var i = 0; i < entries.length; i++) {
            if (!entries[i].isIntersecting) continue;
            io.unobserve(entries[i].target);
            push('banner_visible', { element_name: entries[i].target.getAttribute('data-sk-banner') });
          }
        },
        { threshold: 0.5 },
      );
      for (var bi = 0; bi < banners.length; bi++) io.observe(banners[bi]);
    }

    /* modal_open / modal_close. The theme's own modals are marked data-sk-modal in Liquid:
     * Dawn's show with an `open` attribute, the cart notification with an `active` class.
     * Two come from outside the theme and are recognised here:
     *   - PreProduct's pre-order panel, #pledge-details, which it opens by setting its inline
     *     height to 100% and closes by setting it to 0%.
     *   - Shopify's cancellation policy under the checkout button, a <top-level-modal> that
     *     Shopify adds to <body> to open and removes to close.
     * click_text is the control clicked just before it opened, if any. */
    var THIRD_PARTY = {
      'pledge-details': { name: 'pre-order details', type: 'pre-order' },
      'top-level-modal': { name: 'cancellation policy', type: 'pre-order' },
    };
    function modalNamed(el) {
      if (el.hasAttribute('data-sk-modal')) return el;
      var known = THIRD_PARTY[el.id] || THIRD_PARTY[el.tagName.toLowerCase()];
      if (!known) return null;
      el.setAttribute('data-sk-modal', known.name);
      el.setAttribute('data-sk-modal-type', known.type);
      return el;
    }
    function modalIsOpen(el) {
      if (el.id === 'pledge-details') return parseFloat(el.style.height) > 0;
      if (el.tagName.toLowerCase() === 'top-level-modal') return el.isConnected;
      return el.hasAttribute('open') || el.classList.contains('active');
    }
    function modalChanged(el, open) {
      var was = el.getAttribute('data-sk-modal-state') === 'open';
      if (open === was) return;
      el.setAttribute('data-sk-modal-state', open ? 'open' : 'closed');
      var name = el.getAttribute('data-sk-modal');
      var type = el.getAttribute('data-sk-modal-type');
      if (open) {
        push('modal_open', {
          click_text: Date.now() - lastClick.at < 2000 ? triggerText(lastClick.el) : null,
          element_name: name,
          element_type: type,
        });
      } else {
        push('modal_close', { element_name: name, element_type: type });
      }
    }
    if ('MutationObserver' in window) {
      new MutationObserver(function (records) {
        for (var i = 0; i < records.length; i++) {
          var r = records[i];
          if (r.type === 'attributes') {
            var el = r.target.nodeType === 1 && modalNamed(r.target);
            if (el) modalChanged(el, modalIsOpen(el));
            continue;
          }
          for (var a = 0; a < r.addedNodes.length; a++) {
            var added = r.addedNodes[a];
            if (added.nodeType !== 1) continue;
            if (added.tagName.toLowerCase() === 'top-level-modal' && modalNamed(added)) modalChanged(added, true);
            viewLists(added);
          }
          for (var d = 0; d < r.removedNodes.length; d++) {
            var gone = r.removedNodes[d];
            if (gone.nodeType === 1 && gone.tagName.toLowerCase() === 'top-level-modal' && gone.hasAttribute('data-sk-modal')) {
              modalChanged(gone, false);
            }
          }
        }
      }).observe(document.documentElement, {
        attributes: true,
        childList: true,
        subtree: true,
        attributeFilter: ['open', 'class', 'style'],
      });
    }

    /* scroll: the first time the page reaches 25, 50, 75 and 100%. */
    var marks = [25, 50, 75, 100];
    var fired = {};
    var firedCount = 0;
    var queuedFrame = false;
    function onScroll() {
      if (queuedFrame) return;
      queuedFrame = true;
      window.requestAnimationFrame(function () {
        queuedFrame = false;
        var pct = scrollPercent(window.scrollY, window.innerHeight, document.documentElement.scrollHeight);
        for (var i = 0; i < marks.length; i++) {
          if (fired[marks[i]] || pct < marks[i]) continue;
          fired[marks[i]] = true;
          firedCount++;
          push('scroll', { scroll_percentage: marks[i], element_type: 'page' });
        }
        if (firedCount === marks.length) window.removeEventListener('scroll', onScroll);
      });
    }
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  // readIdentity never rejects and gives up after 1.5 seconds.
  (hasStatisticsConsent() ? identify() : Promise.resolve()).then(run);
})();
